import React, { useEffect, useState } from 'react';
import { adminOrders, adminUpdateOrder } from '../../lib/api';
import { Loader2, ChevronDown, ChevronRight } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useToast } from '../../hooks/use-toast';

const statusColor = {
  pending: 'bg-yellow-100 text-yellow-800',
  paid: 'bg-blue-100 text-blue-800',
  shipped: 'bg-purple-100 text-purple-800',
  delivered: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const { toast } = useToast();

  const load = () => adminOrders().then(setOrders).catch(console.error).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await adminUpdateOrder(id, status);
      toast({ title: 'Status updated' });
      load();
    } catch { toast({ title: 'Update failed', variant: 'destructive' }); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-neutral-400" /></div>;

  return (
    <div>
      <h1 className="text-2xl font-black mb-1">Orders</h1>
      <p className="text-sm text-neutral-500 mb-6">{orders.length} orders total</p>

      {orders.length === 0 ? (
        <div className="bg-white rounded-lg p-12 text-center border">
          <p className="text-neutral-500">No orders yet. They will appear here as customers check out.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-left text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="px-4 py-3"></th>
                <th className="px-4 py-3">Order</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Total</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Date</th>
              </tr>
            </thead>
            <tbody>
              {orders.map(o => (
                <React.Fragment key={o.id}>
                  <tr className="border-t hover:bg-neutral-50 cursor-pointer" onClick={() => setExpanded(expanded === o.id ? null : o.id)}>
                    <td className="px-4 py-3">{expanded === o.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</td>
                    <td className="px-4 py-3 font-mono text-xs">#{o.id.slice(0, 8)}</td>
                    <td className="px-4 py-3">
                      <div className="font-semibold">{o.customer?.name}</div>
                      <div className="text-xs text-neutral-500">{o.customer?.email}</div>
                    </td>
                    <td className="px-4 py-3 font-bold">${(o.total || 0).toFixed(2)}</td>
                    <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                      <Select value={o.status} onValueChange={(v) => updateStatus(o.id, v)}>
                        <SelectTrigger className="w-32 h-8">
                          <Badge className={`${statusColor[o.status] || 'bg-neutral-100'} hover:${statusColor[o.status]} capitalize`}>{o.status}</Badge>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="paid">Paid</SelectItem>
                          <SelectItem value="shipped">Shipped</SelectItem>
                          <SelectItem value="delivered">Delivered</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3 text-xs text-neutral-500">{new Date(o.created_at).toLocaleString()}</td>
                  </tr>
                  {expanded === o.id && (
                    <tr className="bg-neutral-50">
                      <td colSpan={6} className="px-6 py-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <h4 className="text-xs font-bold uppercase text-neutral-500 mb-2">Items</h4>
                            <ul className="space-y-1 text-sm">
                              {(o.items || []).map((it, i) => (
                                <li key={i} className="flex justify-between">
                                  <span>{it.qty} × {it.name}{it.flavor ? ` (${it.flavor})` : ''}</span>
                                  <span className="font-semibold">${(it.price * it.qty).toFixed(2)}</span>
                                </li>
                              ))}
                            </ul>
                            <div className="mt-3 pt-3 border-t text-sm space-y-1">
                              <div className="flex justify-between"><span>Subtotal</span><span>${(o.subtotal || 0).toFixed(2)}</span></div>
                              <div className="flex justify-between"><span>Shipping</span><span>${(o.shipping || 0).toFixed(2)}</span></div>
                              <div className="flex justify-between font-bold"><span>Total</span><span>${(o.total || 0).toFixed(2)}</span></div>
                            </div>
                          </div>
                          <div>
                            <h4 className="text-xs font-bold uppercase text-neutral-500 mb-2">Customer</h4>
                            <div className="text-sm space-y-1">
                              <div><span className="text-neutral-500">Name:</span> {o.customer?.name}</div>
                              <div><span className="text-neutral-500">Email:</span> <a href={`mailto:${o.customer?.email}`} className="text-blue-600 hover:underline">{o.customer?.email}</a></div>
                              {o.customer?.phone && <div><span className="text-neutral-500">Phone:</span> <a href={`tel:${o.customer.phone}`} className="text-blue-600 hover:underline">{o.customer.phone}</a></div>}
                              {o.customer?.address && <div><span className="text-neutral-500">Address:</span> {o.customer.address}</div>}
                              <div className="font-mono text-xs text-neutral-500 mt-2">Order ID: {o.id}</div>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminOrders;
